import { useFilterList } from '@/hooks/FilterModal';
import { Fragment, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useStyle } from '@/classify';
import FilterItem from './filterItem';
import FilterItemRadioGroup from './filterItemRadioGroup';
import defaultClasses from './filterList.module.css';

import ColorItem from './colorItem';
import ColumnItem from './columnItem';
import PriceSelect from './priceSelect';
import SizeItem from './sizeItem';

const labels = new WeakMap();

const FilterList = props => {
  const {
    filterApi,
    filterState,
    filterFrontendInput,
    name,
    group,
    itemCountToShow = 999, // 5
    items,
    onApply,
    onFilterItemMouseDown,
    currentCurrencyCode,
    handleGenerateLinkHref,
  } = props;

  const classes = useStyle(defaultClasses, props.classes);
  const talonProps = useFilterList({
    filterState,
    items,
    itemCountToShow,
  });
  const { isListExpanded, handleListToggle } = talonProps;
  const { formatMessage } = useIntl();

  // memoize item creation
  // search value is not referenced, so this array is stable

  const defaultItemElements = useMemo(() => {
    if (filterFrontendInput === 'boolean') {
      const key = `item-${group}`;

      return (
        <li
          key={key}
          className={classes.item}
        >
          <FilterItemRadioGroup
            filterApi={filterApi}
            filterState={filterState}
            group={group}
            name={name}
            items={items}
            onApply={onApply}
            labels={labels}
          />
        </li>
      );
    }

    return items.map((item, index) => {
      const { title, value } = item;
      const key = `item-${group}-${value}`;

      if (!isListExpanded && index >= itemCountToShow) {
        return null;
      }

      // create an element for each item
      const element = (
        <li
          key={key}
          className={classes.item}
        >
          <FilterItem
            filterApi={filterApi}
            filterState={filterState}
            group={group}
            item={item}
            onApply={onApply}
            onFilterItemMouseDown={onFilterItemMouseDown}
          />
        </li>
      );

      // associate each element with its normalized title
      // titles are not unique, so use the element as the key
      labels.set(element, title.toUpperCase());

      return element;
    });
  }, [
    filterFrontendInput,
    items,
    group,
    classes.item,
    filterApi,
    filterState,
    name,
    onApply,
    isListExpanded,
    itemCountToShow,
    onFilterItemMouseDown,
  ]);

  const sizeElements = useMemo(() => {
    return items.map((item, index) => {
      const { title, value } = item;
      const key = `item-${group}-${value}`;

      if (!isListExpanded && index >= itemCountToShow) {
        return null;
      }

      const element = (
        <li
          key={key}
          className={classes.itemSize}
        >
          <SizeItem
            filterApi={filterApi}
            filterState={filterState}
            group={group}
            item={item}
            onApply={onApply}
            onFilterItemMouseDown={onFilterItemMouseDown}
            handleGenerateLinkHref={handleGenerateLinkHref}
          />
        </li>
      );

      // associate each element with its normalized title
      // titles are not unique, so use the element as the key
      labels.set(element, title.toUpperCase());

      return element;
    });
  }, [
    items,
    group,
    isListExpanded,
    itemCountToShow,
    classes.itemSize,
    filterApi,
    filterState,
    onApply,
    onFilterItemMouseDown,
  ]);

  const columnElements = useMemo(() => {
    return items.map((item, index) => {
      const { title, value } = item;
      const key = `item-${group}-${value}`;

      if (!isListExpanded && index >= itemCountToShow) {
        return null;
      }

      // create an element for each item
      const element = (
        <li
          key={key}
          className={classes.itemColumn}
        >
          <ColumnItem
            filterApi={filterApi}
            filterState={filterState}
            group={group}
            item={item}
            onApply={onApply}
            onFilterItemMouseDown={onFilterItemMouseDown}
            handleGenerateLinkHref={handleGenerateLinkHref}
          />
        </li>
      );

      // associate each element with its normalized title
      // titles are not unique, so use the element as the key
      labels.set(element, title.toUpperCase());

      return element;
    });
  }, [
    items,
    group,
    isListExpanded,
    itemCountToShow,
    classes.itemColumn,
    filterApi,
    filterState,
    onApply,
    onFilterItemMouseDown,
  ]);

  const colorElements = useMemo(() => {
    return items.map((item, index) => {
      const { title, value } = item;
      const key = `item-${group}-${value}`;

      if (!isListExpanded && index >= itemCountToShow) {
        return null;
      }

      // create an element for each item
      const element = (
        <li
          key={key}
          className={classes.itemSize}
        >
          <ColorItem
            filterApi={filterApi}
            filterState={filterState}
            group={group}
            item={item}
            onApply={onApply}
            onFilterItemMouseDown={onFilterItemMouseDown}
          />
        </li>
      );

      // associate each element with its normalized title
      // titles are not unique, so use the element as the key
      labels.set(element, title.toUpperCase());

      return element;
    });
  }, [
    items,
    group,
    isListExpanded,
    itemCountToShow,
    classes.itemSize,
    filterApi,
    filterState,
    onApply,
    onFilterItemMouseDown,
  ]);

  const priceElements = useMemo(() => {
    return (
      <PriceSelect
        prices={items}
        filterApi={filterApi}
        filterState={filterState}
        group={group}
        onApply={onApply}
        currentCurrencyCode={currentCurrencyCode}
      />
    );
  }, [items, filterApi, filterState, group, onApply, currentCurrencyCode]);

  const itemElementsClass = {
    // Color: colorElements,
    Color: columnElements,
    Size: sizeElements,
    Price: priceElements,
    'Shop By Styles': columnElements,
    'Shop By Function': columnElements,
    Material: columnElements,
  };

  const itemElements = itemElementsClass[name] || columnElements || defaultItemElements;

  const showMoreLessItem = useMemo(() => {
    if (items.length <= itemCountToShow) {
      return null;
    }

    if (name === 'Price') {
      return null;
    }

    const label = isListExpanded
      ? formatMessage({
          id: 'filterList.showLess',
          defaultMessage: 'Show Less',
        })
      : formatMessage({
          id: 'filterList.showMore',
          defaultMessage: 'Show More',
        });

    return (
      <li className={classes.showMoreLessItem}>
        <button
          onClick={handleListToggle}
          className={classes.showMoreLessButton}
        >
          {label}
        </button>
      </li>
    );
  }, [
    items.length,
    itemCountToShow,
    name,
    isListExpanded,
    formatMessage,
    classes.showMoreLessItem,
    classes.showMoreLessButton,
    handleListToggle,
  ]);

  const defaultItems = {
    // Color: classes.colorItems,
    Color: classes.columnItems,
    Size: classes.sizeItems,
    Price: classes.priceItems,
    'Shop By Styles': classes.columnItems,
    'Shop By Function': classes.columnItems,
    Material: classes.columnItems,
  };

  const initialItems = defaultItems[name] || classes.items;

  return (
    <Fragment>
      <ul className={initialItems}>{itemElements}</ul>
      {showMoreLessItem}
    </Fragment>
  );
};

export default FilterList;
